import { useEffect } from 'react';
import { useQuoteModal } from '../../../../context/QuoteModalContext';

export interface TentData {
  name: string;
  image: string;
  dimensions: string;
  sqft: string;
  capacity: string[];
}

interface TentLightboxProps {
  tents: TentData[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

export default function TentLightbox({
  tents,
  currentIndex,
  onClose,
  onPrev,
  onNext,
}: TentLightboxProps) {
  const tent = tents[currentIndex];
  const { openModal } = useQuoteModal();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl overflow-hidden max-w-4xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {/* Image area */}
        <div className="relative h-[480px] bg-gray-50 flex items-center justify-center">
          <img
            src={tent.image}
            alt={tent.name}
            className="max-w-full max-h-full object-contain"
          />

          {/* Prev arrow */}
          <button
            onClick={onPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/85 hover:bg-white text-gray-800 rounded-full flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </button>

          {/* Next arrow */}
          <button
            onClick={onNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/85 hover:bg-white text-gray-800 rounded-full flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>

        {/* Details */}
        <div className="p-6 flex items-start justify-between gap-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{tent.name}</h3>
            <p className="text-gray-400 text-sm mb-4">
              {tent.dimensions} &nbsp;·&nbsp; {tent.sqft}
            </p>
            <div className="space-y-1.5">
              {tent.capacity.map((cap, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-user-line text-teal-400"></i>
                  {cap}
                </div>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={openModal}
            className="bg-teal-400 hover:bg-teal-500 text-white px-7 py-3 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer"
          >
            Inquire Now
          </button>
        </div>

        {/* Progress dots */}
        <div className="px-6 pb-5 flex gap-1.5">
          {tents.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === currentIndex ? 'bg-teal-400 w-7' : 'bg-gray-200 w-3'
              }`}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
}
